import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiService } from '../../services/api';
import { KGInstance, DBIndex } from '../../types';
import { extractErrorMessage } from '../../utils/errorHandler';
import '../shared.css';
import '../KG/Modal.css';
import './CustomIndexManager.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CustomIndexManagerProps {
  kgId: string;
  versionId: string;
}

export type IndexType = 'fulltext' | 'range' | 'composite';

interface IndexTypeDef {
  value: IndexType;
  label: string;
  description: string;
}

const INDEX_TYPES: IndexTypeDef[] = [
  { value: 'range',     label: 'Range',     description: 'Range / equality queries: numbers, dates, booleans, strings (default)' },
  { value: 'fulltext',  label: 'Fulltext',  description: 'Full-text search: STRING properties only' },
  { value: 'composite', label: 'Composite', description: 'Multi-property equality lookups: any scalar type' },
];

// Property types compatible with each index type (lowercase Neo4j type names).
// Empty string '' covers unknown/untyped schema properties — allow them through.
const ALLOWED_PROP_TYPES: Record<IndexType, Set<string>> = {
  range:     new Set(['string', 'str', 'integer', 'int', 'long', 'float', 'double', 'boolean', 'bool', 'date', 'datetime', 'localdatetime', 'localtime', 'time', 'duration', '']),
  fulltext:  new Set(['string', 'str', '']),
  composite: new Set(['string', 'str', 'integer', 'int', 'long', 'float', 'double', 'boolean', 'bool', 'date', 'datetime', 'localdatetime', 'localtime', 'time', 'duration', '']),
};

// Default pre-selected properties per index type.
// Fulltext only pre-selects 'name' — 'id' is typically a number which isn't STRING-compatible.
const DEFAULT_PROPERTIES_BY_TYPE: Record<IndexType, string[]> = {
  range:     ['name', 'id'],
  fulltext:  ['name'],
  composite: ['name', 'id'],
};
// Union for UI checks (chip highlighting etc.)
const DEFAULT_PROPERTIES = ['name', 'id'];

interface SchemaProperty { name: string; type: string; }
interface NodeSchema     { label: string; properties: SchemaProperty[]; }
interface NodeState      { selectedProps: string[]; creating: boolean; error: string; }
type ViewMode = 'create' | 'manage';

interface BatchResult {
  total: number;
  done: number;
  succeeded: string[];
  failed: { label: string; error: string }[];
  awaitingOnline: boolean; // true after requests sent, waiting for indexes to come online
}

interface DeleteResult {
  total: number;
  done: number;
  succeeded: number;
  failed: string[]; // index names that failed
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function isCompatible(prop: SchemaProperty, indexType: IndexType): boolean {
  return ALLOWED_PROP_TYPES[indexType].has((prop.type || '').toLowerCase());
}

function defaultSelectedProps(node: NodeSchema, indexType: IndexType): string[] {
  const defaults = DEFAULT_PROPERTIES_BY_TYPE[indexType];
  return node.properties
    .filter((p) => defaults.includes(p.name) && isCompatible(p, indexType))
    .map((p) => p.name);
}

function parseSchema(raw: any): NodeSchema[] {
  const schemaData: any =
    raw?.data?.attributes?.schema ||
    raw?.data?.schema ||
    raw?.data?.attributes ||
    raw?.data ||
    {};

  const extractProps = (propsObj: any): SchemaProperty[] => {
    if (!propsObj || typeof propsObj !== 'object') return [];
    if (Array.isArray(propsObj)) return propsObj.map((p: any) => ({ name: String(p), type: '' }));
    return Object.entries(propsObj).map(([key, val]) => ({
      name: key,
      type: typeof val === 'object' && val !== null ? ((val as any).type ?? '') : '',
    }));
  };

  const result: NodeSchema[] = [];

  if (schemaData.nodes && typeof schemaData.nodes === 'object') {
    for (const label of Object.keys(schemaData.nodes)) {
      const nd = schemaData.nodes[label];
      result.push({ label, properties: nd?.properties ? extractProps(nd.properties) : extractProps(nd) });
    }
  } else if (schemaData.nodeProperties && typeof schemaData.nodeProperties === 'object') {
    for (const label of Object.keys(schemaData.nodeProperties)) {
      result.push({ label, properties: extractProps(schemaData.nodeProperties[label]) });
    }
  } else if (Array.isArray(schemaData.nodeLabels)) {
    for (const label of schemaData.nodeLabels as string[]) {
      result.push({ label, properties: [] });
    }
  } else if (typeof schemaData === 'object') {
    // Exclude well-known non-node top-level keys that appear in some schema shapes
    const NON_NODE_KEYS = new Set(['edges', 'relationships', 'metadata', 'version', 'indexes', 'constraints']);
    for (const [key, val] of Object.entries(schemaData)) {
      if (!NON_NODE_KEYS.has(key) && typeof val === 'object' && val !== null && !Array.isArray(val))
        result.push({ label: key, properties: extractProps(val) });
    }
  }

  return result.sort((a, b) => a.label.localeCompare(b.label));
}

function parseIndexes(raw: any): DBIndex[] {
  if (!Array.isArray(raw?.data)) return [];
  return raw.data.map((item: any) => {
    const attrs = item.attributes ?? item;
    return {
      name:              attrs.name ?? attrs.index_name ?? item.id ?? '',
      type:              attrs.type ?? '',
      entityType:        attrs.entityType ?? attrs.entity_type ?? 'NODE',
      labelsOrTypes:     attrs.labelsOrTypes ?? attrs.labels_or_types ?? [],
      properties:        attrs.properties ?? [],
      state:             attrs.state ?? '',
      populationPercent: attrs.populationPercent ?? attrs.population_percent,
    } as DBIndex;
  });
}

function statusDotClass(state: string | undefined): 'online' | 'populating' | 'failed' | 'other' {
  const s = (state || '').toLowerCase();
  if (!s || s === 'online') return 'online';
  if (s === 'populating')   return 'populating';
  if (s === 'failed' || s === 'error') return 'failed';
  return 'other';
}

// An index is user-manageable if it's on a NODE and wasn't auto-created by Neo4j
function isManageable(idx: DBIndex): boolean {
  return idx.entityType === 'NODE' && idx.type.toUpperCase() !== 'LOOKUP';
}

function formatInstanceLabel(inst: KGInstance): string {
  const type = inst.instance_type || 'instance';
  const tail = inst.instance_id.slice(-10);
  const suffix = inst.default_instance ? ' · default' : '';
  return `${type} · …${tail}${suffix}`;
}

// Sort properties: eligible (not indexed, compatible) first, already-indexed second, incompatible last
function sortedProps(
  properties: SchemaProperty[],
  indexType: IndexType,
  alreadyIndexed: Set<string>
): SchemaProperty[] {
  return [...properties].sort((a, b) => {
    const aExists = alreadyIndexed.has(a.name);
    const bExists = alreadyIndexed.has(b.name);
    const aCompat = isCompatible(a, indexType);
    const bCompat = isCompatible(b, indexType);

    const rank = (exists: boolean, compat: boolean) =>
      exists ? 1 : !compat ? 2 : 0;

    return rank(aExists, aCompat) - rank(bExists, bCompat) || a.name.localeCompare(b.name);
  });
}

// ─── IndexInfoModal ───────────────────────────────────────────────────────────

const INDEX_DOCS = [
  {
    type: 'Range',
    autoPlanned: true,
    summary: 'General-purpose. Best default for most properties.',
    dataTypes: ['String', 'Integer', 'Float', 'Boolean', 'Date', 'DateTime', 'Duration', 'Point'],
    predicates: ['=', '<', '>', '≤', '≥', 'IN', 'STARTS WITH', 'ENDS WITH', 'CONTAINS', 'IS NOT NULL'],
    avoid: 'String values over ~8 KB',
    tip: 'Start here. Switch to Fulltext only when you need word-level search within long text.',
  },
  {
    type: 'Fulltext',
    autoPlanned: false,
    summary: 'Tokenises text, matches words anywhere in the string. Powers search features.',
    dataTypes: ['String', 'List<String>'],
    predicates: ['db.index.fulltext.queryNodes()'],
    avoid: 'Numbers, booleans, dates. Must be called explicitly; not auto-planned by Cypher.',
    tip: 'Only pre-selects "name" by default. ~30 to 100% storage overhead. Combine with Range for hybrid search.',
  },
  {
    type: 'Composite',
    autoPlanned: true,
    summary: 'Range index across 2+ properties. Best when queries filter multiple fields together.',
    dataTypes: ['String', 'Integer', 'Float', 'Boolean', 'Date', 'DateTime', 'Duration', 'Point'],
    predicates: ['=', 'IN', '<', '>', 'STARTS WITH'],
    avoid: 'Queries filtering only a subset of indexed properties; the planner skips the index.',
    tip: 'Property order matters: put equality/IN properties first, range predicates after. Highest-cardinality first.',
  },
];

const IndexInfoModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
  <div className="ci-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Index types reference">
    <div className="ci-modal" onClick={(e) => e.stopPropagation()}>
      <div className="ci-modal-header">
        <h3 className="ci-modal-title">Index Types</h3>
        <a
          href="https://neo4j.com/docs/cypher-manual/current/indexes/"
          target="_blank"
          rel="noopener noreferrer"
          className="ci-modal-docs-link"
        >
          Neo4j docs ↗
        </a>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div className="ci-modal-body">
        <div className="ci-modal-cards">
          {INDEX_DOCS.map((doc) => (
            <div key={doc.type} className="ci-modal-card">
              <div className="ci-modal-card-head">
                <span className={`ci-idx-type-badge ci-idx-type-${doc.type.toLowerCase()}`}>{doc.type}</span>
                {doc.autoPlanned
                  ? <span className="ci-modal-tag ci-modal-tag--ok">Auto-planned</span>
                  : <span className="ci-modal-tag ci-modal-tag--warn">Explicit call</span>}
              </div>
              <p className="ci-modal-summary">{doc.summary}</p>
              <div className="ci-modal-row">
                <span className="ci-modal-key">Types</span>
                <div className="ci-modal-chips">
                  {doc.dataTypes.map((t) => (
                    <span key={t} className="ci-modal-chip">{t}</span>
                  ))}
                </div>
              </div>
              <div className="ci-modal-row">
                <span className="ci-modal-key">Predicates</span>
                <div className="ci-modal-chips">
                  {doc.predicates.map((p) => (
                    <span key={p} className="ci-modal-chip ci-modal-chip--mono">{p}</span>
                  ))}
                </div>
              </div>
              <div className="ci-modal-row ci-modal-row--avoid">
                <span className="ci-modal-key">Avoid</span>
                <span className="ci-modal-avoid">{doc.avoid}</span>
              </div>
              <div className="ci-modal-tip">{doc.tip}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
  );
};

// ─── CreateCard ───────────────────────────────────────────────────────────────

interface CreateCardProps {
  node: NodeSchema;
  indexType: IndexType;
  selectedProps: string[];
  alreadyIndexed: Set<string>;
  creating: boolean;
  error: string;
  onToggle: (label: string, prop: string) => void;
  onSelectAll: (label: string) => void;
}

const CreateCard: React.FC<CreateCardProps> = ({
  node, indexType, selectedProps, alreadyIndexed, creating, error, onToggle, onSelectAll,
}) => {
  const ordered  = useMemo(() => sortedProps(node.properties, indexType, alreadyIndexed), [node.properties, indexType, alreadyIndexed]);
  const eligible = ordered.filter((p) => isCompatible(p, indexType) && !alreadyIndexed.has(p.name));
  const allSelected = eligible.length > 0 && eligible.every((p) => selectedProps.includes(p.name));
  const nSelected = selectedProps.length;

  return (
    <div className={`ci-card${creating ? ' ci-card--busy' : ''}${nSelected > 0 ? ' ci-card--staged' : ''}`}>
      <div className="ci-card-head">
        <span className="ci-node-name" title={node.label}>{node.label}</span>
        <div className="ci-head-right">
          <span className={`ci-idx-type-badge ci-idx-type-${indexType}`}>{indexType}</span>
          {nSelected > 0 && <span className="ci-selected-badge">{nSelected}</span>}
          {creating && <span className="ci-spinner ci-spinner--sm ci-spinner--purple" />}
          {eligible.length > 0 && !creating && (
            <button className="ci-select-all-btn" onClick={() => onSelectAll(node.label)}>
              {allSelected ? 'None' : 'All'}
            </button>
          )}
        </div>
      </div>

      {creating ? (
        <p className="ci-busy-msg">Indexing {selectedProps.join(', ')}…</p>
      ) : (
        <div className="ci-props-wrap">
          {ordered.map((prop) => {
            const compatible    = isCompatible(prop, indexType);
            const alreadyExists = alreadyIndexed.has(prop.name);
            const checked       = selectedProps.includes(prop.name);
            const isDefault     = DEFAULT_PROPERTIES.includes(prop.name) && compatible;
            const disabled      = !compatible || alreadyExists;
            const typeLabel     = prop.type || 'unknown type';

            const title = alreadyExists
              ? `${prop.name} already has a ${indexType} index. Remove it in Manage first`
              : !compatible
              ? `${typeLabel}: not compatible with ${indexType} index`
              : undefined;

            const pillClass = [
              'ci-pill',
              alreadyExists               ? ' ci-pill--exists'  : '',
              !alreadyExists && checked   ? ' ci-pill--on'      : '',
              isDefault && !alreadyExists ? ' ci-pill--default' : '',
              disabled                    ? ' ci-pill--off'     : '',
            ].join('');

            return (
              <label key={prop.name} className={pillClass} title={title}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggle(node.label, prop.name)}
                />
                <span className="ci-pill-name">{prop.name}</span>
                {prop.type && (
                  <span className={`ci-type ci-type-${prop.type.toLowerCase()}`}>{prop.type}</span>
                )}
                {alreadyExists && <span className="ci-pill-indexed">✓</span>}
              </label>
            );
          })}
        </div>
      )}

      {error && <div className="ci-card-error">{error}</div>}
    </div>
  );
};

// ─── ManageRow ────────────────────────────────────────────────────────────────

interface ManageRowProps {
  idx: DBIndex;
  selected: boolean;
  bulkMode: boolean;
  onToggleSelect: (name: string) => void;
  onDelete: (name: string) => void;
  deleting: boolean;
}

const ManageRow: React.FC<ManageRowProps> = ({ idx, selected, bulkMode, onToggleSelect, onDelete, deleting }) => {
  const [confirming, setConfirming] = useState(false);
  const dotCls      = statusDotClass(idx.state);
  const isPopulating = dotCls === 'populating';
  const isFailed     = dotCls === 'failed';

  useEffect(() => { if (bulkMode) setConfirming(false); }, [bulkMode]);

  return (
    <tr className={[
      'ci-mgmt-row',
      confirming  ? ' ci-mgmt-row--confirming'  : '',
      selected    ? ' ci-mgmt-row--selected'    : '',
      deleting    ? ' ci-mgmt-row--deleting'    : '',
      isPopulating ? ' ci-mgmt-row--populating' : '',
    ].join('')}>
      <td className="ci-mgmt-td-check">
        <input
          type="checkbox"
          className="ci-mgmt-check"
          checked={selected}
          disabled={deleting || isPopulating}
          title={isPopulating ? 'Unavailable while building' : undefined}
          onChange={() => onToggleSelect(idx.name)}
        />
      </td>
      <td className="ci-mgmt-td-status">
        <span
          className={`ci-dot ci-dot--${dotCls}`}
          title={isPopulating ? 'Building: index is being created' : isFailed ? 'Failed' : 'Online'}
        />
        {isFailed && <span className="ci-status-label ci-status-label--failed">Failed</span>}
      </td>
      <td className="ci-mgmt-td-node">
        <span className="ci-mgmt-node">{idx.labelsOrTypes.join(', ')}</span>
      </td>
      <td className="ci-mgmt-td-props">
        <div className="ci-mgmt-props">
          {idx.properties.map((p) => (
            <span key={p} className={`ci-chip${DEFAULT_PROPERTIES.includes(p) ? ' ci-chip--default' : ''}`}>{p}</span>
          ))}
        </div>
      </td>
      <td className="ci-mgmt-td-type">
        {(() => {
          const isComposite = (idx.type || '').toUpperCase() === 'RANGE' && idx.properties.length >= 2;
          const displayType = isComposite ? 'COMPOSITE (RANGE)' : (idx.type || 'Unknown');
          const badgeCls = isComposite ? 'composite' : (idx.type || 'unknown').toLowerCase();
          return <span className={`ci-idx-type-badge ci-idx-type-${badgeCls}`}>{displayType}</span>;
        })()}
      </td>
      <td className="ci-mgmt-td-action">
        {deleting ? (
          <span className="ci-spinner ci-spinner--sm ci-spinner--red" />
        ) : isPopulating || bulkMode ? null
          : confirming ? (
          <span className="ci-mgmt-confirm">
            Remove?
            <button className="ci-mgmt-yes" onClick={() => { onDelete(idx.name); setConfirming(false); }}>Yes</button>
            <button className="ci-mgmt-no"  onClick={() => setConfirming(false)}>No</button>
          </span>
        ) : (
          <button className="ci-mgmt-del" onClick={() => setConfirming(true)}>Remove</button>
        )}
      </td>
    </tr>
  );
};

// ─── StatusLegend ─────────────────────────────────────────────────────────────

const StatusLegend: React.FC = () => (
  <div className="ci-legend">
    <span className="ci-legend-item"><span className="ci-dot ci-dot--online" /> Online</span>
    <span className="ci-legend-item"><span className="ci-dot ci-dot--populating" /> Building</span>
    <span className="ci-legend-item"><span className="ci-dot ci-dot--failed" /> Failed</span>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CustomIndexManager: React.FC<CustomIndexManagerProps> = ({ kgId, versionId }) => {
  const [mode, setMode]                         = useState<ViewMode>('create');
  const [indexType, setIndexType]               = useState<IndexType>('range');
  const [instances, setInstances]               = useState<KGInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [loadingInstances, setLoadingInstances] = useState(true);
  const [nodes, setNodes]                       = useState<NodeSchema[]>([]);
  const [loadingSchema, setLoadingSchema]       = useState(false);
  const [indexes, setIndexes]                   = useState<DBIndex[]>([]);
  const [loadingIndexes, setLoadingIndexes]     = useState(false);
  const [refreshingIndexes, setRefreshingIndexes] = useState(false);
  const [pageError, setPageError]               = useState('');
  const [nodeState, setNodeState]               = useState<Record<string, NodeState>>({});
  const [creating, setCreating]                 = useState(false);
  const [deletingIndex, setDeletingIndex]       = useState<string | null>(null);
  const [selectedRows, setSelectedRows]         = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting]         = useState(false);
  const [batchResult, setBatchResult]           = useState<BatchResult | null>(null);
  const [deleteResult, setDeleteResult]         = useState<DeleteResult | null>(null);
  const [showInfo, setShowInfo]                 = useState(false);

  // Auto-dismiss timer refs for success banners
  const dismissTimerRef       = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deleteDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AbortController for loadIndexes — cancels in-flight requests on unmount or re-trigger
  const indexAbortRef = useRef<AbortController | null>(null);

  // Keep a ref to the current indexType so loadSchema always reads the latest
  // value without needing it in its dep array (avoids re-creating the callback
  // on every type-change, which would re-trigger the selectedInstance effect).
  const indexTypeRef = useRef<IndexType>(indexType);
  useEffect(() => { indexTypeRef.current = indexType; }, [indexType]);

  // ── Data loading ─────────────────────────────────────────────────────────────

  const loadInstances = useCallback(async () => {
    try {
      setLoadingInstances(true);
      const res: any = await apiService.listInstances(kgId, versionId);
      if (!res.data) return;

      const list: KGInstance[] = res.data.map((item: any) => {
        const a = item.attributes ?? item;
        return {
          instance_id:      a.instance_id ?? item.id,
          kg_id:            a.kg_id ?? kgId,
          version_id:       a.version_id ?? versionId,
          instance_type:    a.instance_type ?? '',
          status:           a.status ?? '',
          is_terminated:    a.is_terminated ?? false,
          default_instance: a.default_instance ?? false,
          created_at:       a.created_at ?? 0,
          org_id:           a.org_id ?? '',
        };
      });

      const running = list.filter((i) => ['running', 'active'].includes(i.status.toLowerCase()));
      setInstances(running);
      const defaultInst = running.find((i) => i.default_instance) ?? running[0];
      if (defaultInst) setSelectedInstance(defaultInst.instance_id);
    } catch (err) {
      setPageError(extractErrorMessage(err, 'Failed to load instances'));
    } finally {
      setLoadingInstances(false);
    }
  }, [kgId, versionId]);

  const loadSchema = useCallback(async () => {
    try {
      setLoadingSchema(true);
      const res: any = await apiService.getSchema(kgId, versionId);
      const parsed = parseSchema(res);
      // Read indexType from ref so we always get the current value without
      // adding indexType to deps (which would needlessly re-create this callback
      // and re-trigger the selectedInstance effect on every type switch).
      const currentIndexType = indexTypeRef.current;
      setNodes(parsed);
      setNodeState((prev) => {
        const next: Record<string, NodeState> = {};
        parsed.forEach((n) => {
          next[n.label] = prev[n.label] ?? {
            selectedProps: defaultSelectedProps(n, currentIndexType),
            creating: false,
            error: '',
          };
        });
        return next;
      });
    } catch (err) {
      setPageError(extractErrorMessage(err, 'Failed to load schema'));
    } finally {
      setLoadingSchema(false);
    }
  }, [kgId, versionId]); // indexTypeRef is a ref — reads are always current, no dep needed

  const loadIndexes = useCallback(async (background = false) => {
    if (!selectedInstance) return;
    indexAbortRef.current?.abort();
    const controller = new AbortController();
    indexAbortRef.current = controller;
    const { signal } = controller;
    try {
      background ? setRefreshingIndexes(true) : setLoadingIndexes(true);
      setPageError('');
      const res: any = await apiService.listDBIndexes(kgId, versionId, selectedInstance);
      if (signal.aborted) return;
      setIndexes(parseIndexes(res));
      if (!background) setSelectedRows(new Set());
    } catch (err) {
      if (signal.aborted) return;
      setPageError(extractErrorMessage(err, 'Failed to load indexes'));
    } finally {
      if (!signal.aborted) {
        background ? setRefreshingIndexes(false) : setLoadingIndexes(false);
      }
    }
  }, [kgId, versionId, selectedInstance]);

  useEffect(() => { loadInstances(); }, [loadInstances]);
  useEffect(() => { if (selectedInstance) { loadSchema(); loadIndexes(); } }, [selectedInstance]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset property selections when index type changes.
  // Uses a ref for nodes so this effect only fires on indexType changes and
  // never reads a stale nodes snapshot from a suppressed dep.
  const nodesRef = useRef<NodeSchema[]>(nodes);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);

  useEffect(() => {
    setNodeState(() => {
      const next: Record<string, NodeState> = {};
      nodesRef.current.forEach((n) => {
        next[n.label] = { selectedProps: defaultSelectedProps(n, indexType), creating: false, error: '' };
      });
      return next;
    });
  }, [indexType]);

  // ── Derived (memoised) ───────────────────────────────────────────────────────

  const nodeIndexList  = useMemo(() => indexes.filter(isManageable), [indexes]);

  const nodeIndexMap   = useMemo(() => {
    const map: Record<string, DBIndex[]> = {};
    nodeIndexList.forEach((idx) => {
      idx.labelsOrTypes.forEach((label) => { (map[label] ??= []).push(idx); });
    });
    return map;
  }, [nodeIndexList]);

  const alreadyIndexedByNode = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    nodes.forEach((n) => {
      const props = (nodeIndexMap[n.label] ?? [])
        .filter((idx) => {
          const t = idx.type.toLowerCase();
          if (indexType === 'composite') {
            // In composite mode, a prop is "already indexed" if it's part of a multi-prop RANGE index
            return t === 'range' && idx.properties.length >= 2;
          }
          if (indexType === 'range') {
            // In range mode, only single-prop RANGE indexes block re-creation
            // Multi-prop RANGE (composite) indexes should NOT block creating a standalone RANGE index
            return t === 'range' && idx.properties.length === 1;
          }
          return t === indexType;
        })
        .flatMap((idx) => idx.properties);
      map[n.label] = new Set(props);
    });
    return map;
  }, [nodeIndexMap, nodes, indexType]);

  const totalExisting  = nodeIndexList.length;
  const onlineCount    = useMemo(() => nodeIndexList.filter((i) => !i.state || i.state.toLowerCase() === 'online').length, [nodeIndexList]);
  // For composite: one API call per node (all props bundled). For others: one call per property.
  const totalToCreate  = useMemo(() => nodes.reduce((s, n) => {
    const count = nodeState[n.label]?.selectedProps.length ?? 0;
    if (count === 0) return s;
    return s + (indexType === 'composite' ? 1 : count);
  }, 0), [nodes, nodeState, indexType]);
  const hasPopulating  = useMemo(() => nodeIndexList.some((i) => statusDotClass(i.state) === 'populating'), [nodeIndexList]);
  const selectableList = useMemo(() => nodeIndexList.filter((i) => statusDotClass(i.state) !== 'populating'), [nodeIndexList]);
  const allRowsSelected  = selectableList.length > 0 && selectedRows.size === selectableList.length;
  const someRowsSelected = selectedRows.size > 0 && !allRowsSelected;

  // Strip already-indexed props from selections after a refresh
  useEffect(() => {
    setNodeState((prev) => {
      const next = { ...prev };
      for (const label of Object.keys(next)) {
        const already = alreadyIndexedByNode[label];
        if (!already || already.size === 0) continue;
        const filtered = next[label].selectedProps.filter((p) => !already.has(p));
        if (filtered.length !== next[label].selectedProps.length) {
          next[label] = { ...next[label], selectedProps: filtered };
        }
      }
      return next;
    });
  }, [alreadyIndexedByNode]);

  // Clear selectedRows entries that no longer exist in the index list (e.g. after bulk delete)
  useEffect(() => {
    setSelectedRows((prev) => {
      if (prev.size === 0) return prev;
      const existing = new Set(nodeIndexList.map((i) => i.name));
      const filtered = new Set(Array.from(prev).filter((name) => existing.has(name)));
      return filtered.size === prev.size ? prev : filtered;
    });
  }, [nodeIndexList]);

  // Auto-poll while any indexes are populating — stops when all online
  useEffect(() => {
    if (!hasPopulating || creating) return;
    const id = setInterval(() => { loadIndexes(true); }, 5000);
    return () => clearInterval(id);
  }, [hasPopulating, creating, loadIndexes]);

  // Clear "awaiting online" state once all populating indexes come online.
  // Depend on batchResult (the object) rather than the optional-chain expression
  // so React reliably detects when awaitingOnline flips.
  useEffect(() => {
    if (batchResult?.awaitingOnline && !hasPopulating) {
      setBatchResult((prev) => prev ? { ...prev, awaitingOnline: false } : null);
    }
  }, [hasPopulating, batchResult]);

  // Auto-dismiss success-only create banner after 4s.
  // Clear any running timer before (re-)starting to prevent duplicate timers
  // accumulating across re-renders while the condition stays true.
  useEffect(() => {
    if (batchResult && !batchResult.awaitingOnline && !creating && batchResult.failed.length === 0) {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => { setBatchResult(null); dismissTimerRef.current = null; }, 4000);
      return () => { if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null; } };
    }
  }, [batchResult, creating]);

  // Auto-dismiss success-only delete banner after 4s.
  useEffect(() => {
    if (deleteResult && deleteResult.done === deleteResult.total && deleteResult.failed.length === 0) {
      if (deleteDismissTimerRef.current) clearTimeout(deleteDismissTimerRef.current);
      deleteDismissTimerRef.current = setTimeout(() => { setDeleteResult(null); deleteDismissTimerRef.current = null; }, 4000);
      return () => { if (deleteDismissTimerRef.current) { clearTimeout(deleteDismissTimerRef.current); deleteDismissTimerRef.current = null; } };
    }
  }, [deleteResult]);

  // Cleanup on unmount: abort any in-flight index load and clear dismiss timers
  useEffect(() => {
    return () => {
      indexAbortRef.current?.abort();
      if (dismissTimerRef.current)       clearTimeout(dismissTimerRef.current);
      if (deleteDismissTimerRef.current) clearTimeout(deleteDismissTimerRef.current);
    };
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const pollWithAbort = useCallback(async (queryId: string): Promise<{ status: string }> => {
    return apiService.pollIndexOperation(kgId, versionId, queryId);
  }, [kgId, versionId]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleToggleProp = useCallback((label: string, prop: string) => {
    setNodeState((prev) => {
      const cur = prev[label];
      if (!cur) return prev;
      const already = cur.selectedProps.includes(prop);
      const newProps = already
        ? cur.selectedProps.filter((p) => p !== prop)
        : [...cur.selectedProps, prop];
      return { ...prev, [label]: { ...cur, selectedProps: newProps, error: '' } };
    });
  }, []);

  const handleClearAll = useCallback(() => {
    setNodeState((prev) => {
      const next = { ...prev };
      for (const label of Object.keys(next)) {
        next[label] = { ...next[label], selectedProps: [], error: '' };
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((label: string) => {
    setNodeState((prev) => {
      const node = nodes.find((n) => n.label === label);
      if (!node) return prev;
      const already = alreadyIndexedByNode[label] ?? new Set<string>();
      const eligibleNames = node.properties
        .filter((p) => isCompatible(p, indexType) && !already.has(p.name))
        .map((p) => p.name);
      const cur = prev[label];
      const allSelected = eligibleNames.every((p) => cur.selectedProps.includes(p));
      return { ...prev, [label]: { ...cur, selectedProps: allSelected ? [] : eligibleNames, error: '' } };
    });
  }, [nodes, indexType, alreadyIndexedByNode]);

  const handleToggleSelectRow = useCallback((name: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedRows((prev) =>
      prev.size === selectableList.length
        ? new Set()
        : new Set(selectableList.map((i) => i.name))
    );
  }, [selectableList]);

  const handleDelete = useCallback(async (indexName: string) => {
    try {
      setDeletingIndex(indexName);
      const { query_id } = await apiService.deleteDBIndex(kgId, versionId, selectedInstance, indexName);
      if (query_id) {
        const { status } = await pollWithAbort(query_id);
        if (status === 'FAILED') throw new Error('Delete operation failed');
      }
      await loadIndexes(true);
      setDeleteResult({ total: 1, done: 1, succeeded: 1, failed: [] });
    } catch (err) {
      setPageError(extractErrorMessage(err, `Failed to remove "${indexName}"`));
    } finally {
      setDeletingIndex(null);
    }
  }, [kgId, versionId, selectedInstance, loadIndexes, pollWithAbort]);

  const handleBulkDelete = useCallback(async () => {
    const toDelete = Array.from(selectedRows);
    if (toDelete.length === 0) return;

    setBulkDeleting(true);
    setDeleteResult({ total: toDelete.length, done: 0, succeeded: 0, failed: [] });

    let succeeded = 0;
    const failed: string[] = [];

    for (const name of toDelete) {
      // Mark row as actively being deleted so it animates out
      setDeletingIndex(name);
      try {
        const { query_id } = await apiService.deleteDBIndex(kgId, versionId, selectedInstance, name);
        if (query_id) {
          const { status } = await pollWithAbort(query_id);
          if (status === 'FAILED') throw new Error('Delete operation failed');
        }
        succeeded++;
      } catch {
        failed.push(name);
      }
      setDeletingIndex(null);
      // Refresh after each deletion so the row disappears live
      await loadIndexes(true);
      setDeleteResult({ total: toDelete.length, done: succeeded + failed.length, succeeded, failed: [...failed] });
    }

    setSelectedRows(new Set(failed)); // keep only failed items selected for retry
    setBulkDeleting(false);
  }, [kgId, versionId, selectedInstance, selectedRows, loadIndexes, pollWithAbort]);

  const handleCreateAll = useCallback(async () => {
    // Composite indexes require ALL selected properties in a single API call.
    // Range / fulltext create one index per property.
    type Job =
      | { kind: 'single'; label: string; prop: string }
      | { kind: 'composite'; label: string; props: string[] };

    const jobs: Job[] = [];
    nodes.forEach((n) => {
      const selected = nodeState[n.label]?.selectedProps ?? [];
      if (selected.length === 0) return;
      if (indexType === 'composite') {
        jobs.push({ kind: 'composite', label: n.label, props: selected });
      } else {
        selected.forEach((prop) => jobs.push({ kind: 'single', label: n.label, prop }));
      }
    });
    if (jobs.length === 0) return;

    setCreating(true);
    setBatchResult({ total: jobs.length, done: 0, succeeded: [], failed: [], awaitingOnline: false });
    const succeeded: string[] = [];
    const failed: { label: string; error: string }[] = [];

    const labelsInFlight = new Set(jobs.map((j) => j.label));
    labelsInFlight.forEach((label) =>
      setNodeState((prev) => ({ ...prev, [label]: { ...prev[label], creating: true, error: '' } }))
    );

    for (const job of jobs) {
      const displayKey = job.kind === 'composite'
        ? `${job.label}.[${job.props.join(', ')}]`
        : `${job.label}.${job.prop}`;
      const props = job.kind === 'composite' ? job.props : [job.prop];
      try {
        const { query_id } = await apiService.createDBIndex(kgId, versionId, selectedInstance, job.label, props, indexType);
        if (query_id) {
          const { status } = await pollWithAbort(query_id);
          if (status === 'FAILED') throw new Error('Index creation failed');
        }
        succeeded.push(displayKey);
      } catch (err) {
        const msg = extractErrorMessage(err, 'Failed');
        failed.push({ label: displayKey, error: msg });
        setNodeState((prev) => ({ ...prev, [job.label]: { ...prev[job.label], error: msg } }));
      }
      setBatchResult((prev) =>
        prev ? { ...prev, done: prev.done + 1, succeeded: [...succeeded], failed: [...failed] } : null
      );
    }

    labelsInFlight.forEach((label) =>
      setNodeState((prev) => ({ ...prev, [label]: { ...prev[label], creating: false } }))
    );
    setCreating(false);

    // Refresh index list to pick up new populating indexes, then switch to Manage
    await loadIndexes(true);

    // Only show "awaiting online" if at least one index was actually created
    setBatchResult((prev) =>
      prev ? { ...prev, awaitingOnline: prev.succeeded.length > 0 } : null
    );
    setMode('manage');
  }, [kgId, versionId, selectedInstance, indexType, nodes, nodeState, loadIndexes, pollWithAbort]);

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loadingInstances) {
    return (
      <div className="ci-manager tab-root">
        <div className="tab-loader">
          <span className="tab-spinner" />
        </div>
      </div>
    );
  }

  if (instances.length === 0) {
    return (
      <div className="ci-manager tab-root">
        <div className="tab-empty">
          <div className="tab-empty-icon">⬡</div>
          <div className="tab-empty-title">No running instances</div>
          <div className="tab-empty-body">Launch an instance from the <strong>Instances</strong> tab first.</div>
        </div>
      </div>
    );
  }

  const instanceSelectId = 'ci-instance-select';

  return (
    <div className="ci-manager tab-root">

      {/* Topbar */}
      <div className="tab-topbar">
        <div className="tab-topbar-left">
          <div className="tab-instance-wrap">
            <span className="tab-instance-dot" />
            <select
              id={instanceSelectId}
              className="tab-instance-select"
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
            >
              {instances.map((inst) => (
                <option key={inst.instance_id} value={inst.instance_id}>
                  {formatInstanceLabel(inst)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="tab-topbar-right">
          <button
            className="tab-btn tab-btn--ghost"
            onClick={() => setShowInfo(true)}
            title="Index types: best practices and supported data types"
          >
            Index Types ?
          </button>
        </div>
      </div>

      {showInfo && <IndexInfoModal onClose={() => setShowInfo(false)} />}

      {/* ── Toolbar ── */}
      <div className="ci-toolbar">
        {/* Left: mode toggle */}
        <div className="ci-mode-toggle">
          <button
            className={`ci-mode-btn${mode === 'create' ? ' ci-mode-btn--active' : ''}`}
            onClick={() => setMode('create')}
          >
            Create
          </button>
          <button
            className={`ci-mode-btn${mode === 'manage' ? ' ci-mode-btn--active' : ''}`}
            onClick={() => setMode('manage')}
          >
            Manage
            {totalExisting > 0 && <span className="ci-mode-count">{totalExisting}</span>}
          </button>
        </div>

        {/* Divider */}
        <div className="ci-toolbar-sep" />

        {/* Middle: index type pills — only in create mode */}
        {mode === 'create' && !loadingSchema && nodes.length > 0 && (
          <div className="ci-type-tabs ci-type-tabs--inline">
            {INDEX_TYPES.map((t) => (
              <button
                key={t.value}
                className={`ci-type-tab${indexType === t.value ? ' ci-type-tab--active' : ''}`}
                onClick={() => setIndexType(t.value)}
                title={t.description}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Stats pill */}
        {!loadingSchema && nodes.length > 0 && (
          <div className="ci-stats">
            <span className="ci-stat-item"><strong>{nodes.length}</strong> nodes</span>
            {totalExisting > 0 && (
              <><span className="ci-stat-sep" /><span className="ci-stat-item"><strong>{onlineCount}</strong>/{totalExisting} online</span></>
            )}
            {totalExisting === 0 && (
              <><span className="ci-stat-sep" /><span className="ci-stat-item ci-stat-item--warn">No indexes yet</span></>
            )}
          </div>
        )}

        {/* Right: actions — pushed to far right */}
        <div className="ci-toolbar-actions">
          {mode === 'create' && !loadingSchema && nodes.length > 0 && (
            <>
              {!creating && (
                <button className="ci-clear-btn" onClick={handleClearAll} title="Deselect all properties">
                  Clear all
                </button>
              )}
              <button
                className="ci-create-btn"
                disabled={creating || totalToCreate === 0}
                onClick={handleCreateAll}
                title={totalToCreate === 0 ? 'Select properties first' : `Create ${totalToCreate} index${totalToCreate !== 1 ? 'es' : ''}`}
              >
                {creating
                  ? <><span className="ci-spinner" />Requesting… {batchResult?.done}/{batchResult?.total}</>
                  : <>Create indexes {totalToCreate > 0 && <span className="ci-create-count">{totalToCreate}</span>}</>
                }
              </button>
            </>
          )}

          {mode === 'manage' && (
            <>
              <StatusLegend />
              <button
                className={`ci-refresh-btn${refreshingIndexes || loadingIndexes ? ' ci-refresh-btn--active' : ''}`}
                disabled={loadingIndexes || bulkDeleting}
                onClick={() => loadIndexes()}
                title={bulkDeleting ? 'Bulk delete in progress' : 'Refresh index list'}
              >
                <span className={`ci-refresh-icon${refreshingIndexes || loadingIndexes ? ' ci-refresh-icon--spinning' : ''}`}>↻</span>
                {refreshingIndexes || loadingIndexes ? 'Refreshing…' : 'Refresh'}
              </button>
              {selectedRows.size > 0 && (
                <button
                  className="ci-bulk-del-btn"
                  disabled={bulkDeleting}
                  onClick={handleBulkDelete}
                  title={`Remove ${selectedRows.size} selected index${selectedRows.size > 1 ? 'es' : ''}`}
                >
                  {bulkDeleting
                    ? <><span className="ci-spinner" />Removing…</>
                    : <>Remove selected <span className="ci-create-count">{selectedRows.size}</span></>
                  }
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Progress bar (visible while creating) ── */}
      {creating && batchResult && (
        <div className="ci-progress-wrap">
          <div className="ci-progress-label">
            <span>Requesting indexing for selected properties… <strong>{batchResult.done}/{batchResult.total}</strong></span>
            <span>{Math.round((batchResult.done / batchResult.total) * 100)}%</span>
          </div>
          <div className="ci-progress-track">
            <div
              className="ci-progress-fill ci-progress-fill--striped"
              style={{ width: `${(batchResult.done / batchResult.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Waiting-for-online state — shown after requests are sent, until all indexes come online */}
      {batchResult?.awaitingOnline && !creating && (
        <div className="ci-progress-wrap ci-progress-wrap--waiting">
          <div className="ci-progress-label">
            <span>
              <span className="ci-spinner ci-spinner--sm ci-spinner--green" style={{ marginRight: 6 }} />
              Waiting for indexes to come online…
              {hasPopulating && <span className="ci-progress-sub"> ({nodeIndexList.filter((i) => statusDotClass(i.state) === 'populating').length} building)</span>}
            </span>
            {batchResult.failed.length > 0 && (
              <span className="ci-banner-fail">{batchResult.failed.length} failed</span>
            )}
          </div>
          <div className="ci-progress-track">
            <div className="ci-progress-fill ci-progress-fill--indeterminate" />
          </div>
        </div>
      )}

      {/* ── Batch result banner (shown after awaitingOnline resolves) ── */}
      {batchResult && !creating && !batchResult.awaitingOnline && (
        <div className={`ci-banner${batchResult.failed.length > 0 ? ' ci-banner--warn' : ' ci-banner--ok'}`}>
          <span>
            {batchResult.succeeded.length > 0 && <strong>{batchResult.succeeded.length} indexes created</strong>}
            {batchResult.succeeded.length > 0 && batchResult.failed.length > 0 && '  ·  '}
            {batchResult.failed.length > 0 && <span className="ci-banner-fail">{batchResult.failed.length} failed</span>}
          </span>
          {batchResult.failed.length > 0 && (
            <span className="ci-banner-errors">
              {batchResult.failed.map(({ label, error }) => (
                <span key={label} className="ci-banner-err-item"><b>{label}</b> {error}</span>
              ))}
            </span>
          )}
          <button className="ci-banner-close" onClick={() => setBatchResult(null)}>×</button>
        </div>
      )}

      {/* ── Delete progress bar (bulk delete in flight) ── */}
      {bulkDeleting && deleteResult && (
        <div className="ci-progress-wrap ci-progress-wrap--delete">
          <div className="ci-progress-label">
            <span className="ci-progress-delete-msg">
              <span className="ci-spinner ci-spinner--sm ci-spinner--red" style={{ marginRight: 6 }} />
              {deletingIndex
                ? <>Removing <code className="ci-progress-index-name">{deletingIndex}</code>…</>
                : <>Waiting for confirmation…</>
              }
              <span className="ci-progress-count"> {deleteResult.done}/{deleteResult.total}</span>
              {deleteResult.failed.length > 0 && (
                <span className="ci-banner-fail" style={{ marginLeft: 8 }}>{deleteResult.failed.length} failed</span>
              )}
            </span>
            <span>{Math.round((deleteResult.done / deleteResult.total) * 100)}%</span>
          </div>
          <div className="ci-progress-track">
            <div
              className="ci-progress-fill ci-progress-fill--delete ci-progress-fill--striped-delete"
              style={{ width: `${Math.max((deleteResult.done / deleteResult.total) * 100, 2)}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Delete result banner ── */}
      {deleteResult && !bulkDeleting && (
        <div className={`ci-banner${deleteResult.failed.length > 0 ? ' ci-banner--warn' : ' ci-banner--ok'}`}>
          <span>
            {deleteResult.succeeded > 0 && (
              <strong>{deleteResult.succeeded} index{deleteResult.succeeded !== 1 ? 'es' : ''} removed</strong>
            )}
            {deleteResult.succeeded > 0 && deleteResult.failed.length > 0 && '  ·  '}
            {deleteResult.failed.length > 0 && (
              <span className="ci-banner-fail">{deleteResult.failed.length} failed to remove</span>
            )}
          </span>
          {deleteResult.failed.length > 0 && (
            <span className="ci-banner-errors">
              {deleteResult.failed.map((name) => (
                <span key={name} className="ci-banner-err-item"><b>{name}</b></span>
              ))}
            </span>
          )}
          <button className="ci-banner-close" onClick={() => setDeleteResult(null)}>×</button>
        </div>
      )}

      {pageError && (
        <div className="ci-banner ci-banner--warn">
          <span>{pageError}</span>
          <button className="ci-banner-close" onClick={() => setPageError('')}>×</button>
        </div>
      )}

      {/* ── Content ── */}
      {loadingSchema ? (
        <div className="ci-skeleton-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="ci-skeleton-card">
              <div className="ci-skeleton-head" />
              <div className="ci-skeleton-body">
                {Array.from({ length: 3 + (i % 3) }).map((__, j) => (
                  <div key={j} className="ci-skeleton-pill" style={{ width: `${52 + (j * 17) % 40}px` }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : loadingIndexes && mode === 'manage' ? (
        <div className="ci-skeleton-table">
          <div className="ci-skeleton-table-head" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="ci-skeleton-row" style={{ animationDelay: `${i * 0.07}s` }} />
          ))}
        </div>
      ) : (
        <>
          {mode === 'create' && (
            nodes.length === 0 ? (
              <div className="ci-empty-state">
                <div className="ci-empty-title">No schema available</div>
              </div>
            ) : (
              <div className="ci-grid">
                {nodes.map((node) => {
                  const ns = nodeState[node.label] ?? { selectedProps: [], creating: false, error: '' };
                  return (
                    <CreateCard
                      key={node.label}
                      node={node}
                      indexType={indexType}
                      selectedProps={ns.selectedProps}
                      alreadyIndexed={alreadyIndexedByNode[node.label] ?? new Set()}
                      creating={ns.creating}
                      error={ns.error}
                      onToggle={handleToggleProp}
                      onSelectAll={handleSelectAll}
                    />
                  );
                })}
              </div>
            )
          )}

          {mode === 'manage' && (
            nodeIndexList.length === 0 ? (
              <div className="ci-empty-state">
                <div className="ci-empty-title">No indexes yet</div>
                <div className="ci-empty-body">
                  Switch to{' '}
                  <button className="ci-goto-create-btn" onClick={() => setMode('create')}>
                    Create
                  </button>
                  {' '}to add your first index.
                </div>
              </div>
            ) : (
              <div className="ci-mgmt-wrap">
                <table className="ci-mgmt-table">
                  <thead>
                    <tr>
                      <th className="ci-mgmt-th-check">
                        <input
                          type="checkbox"
                          className="ci-mgmt-check"
                          checked={allRowsSelected}
                          ref={(el) => { if (el) el.indeterminate = someRowsSelected; }}
                          onChange={handleToggleSelectAll}
                        />
                      </th>
                      <th className="ci-mgmt-th-status">Status</th>
                      <th>Node</th>
                      <th>Properties</th>
                      <th>Type</th>
                      <th style={{ width: 110 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {nodeIndexList.map((idx) => (
                      <ManageRow
                        key={idx.name}
                        idx={idx}
                        selected={selectedRows.has(idx.name)}
                        bulkMode={selectedRows.size > 0}
                        onToggleSelect={handleToggleSelectRow}
                        onDelete={handleDelete}
                        deleting={deletingIndex === idx.name}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default CustomIndexManager;
